import { FC, useEffect, useRef, useState } from 'react';

import { MODAL_Z_INDEX } from '../constants';
import { DownTriangle } from '../constants-react';

const CONTAINER_W = 120;

interface PickerProps {
  title: string;
  choices: string[];
  current: string | number;
  setCurrent: (value: string) => void;
  choicesShowing?: boolean;
  setChoicesShowing: (value: boolean) => void;
}

export const Picker: FC<PickerProps> = ({
  title: pickerLabel,
  current,
  choices,
  setCurrent: setCurrentChoice,
  choicesShowing,
  setChoicesShowing,
}) => {
  const toggle = (event: React.MouseEvent): void => {
    setChoicesShowing(!choicesShowing);
    event.stopPropagation();
  };

  const handleListItemClick = (value: string): void => {
    setCurrentChoice(value);
    setChoicesShowing(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <span>{pickerLabel}</span>

      <div
        style={{
          position: 'relative',
          border: '1px solid #337ab7',
          marginTop: '10px',
        }}
      >
        <Current value={current} toggle={toggle} />

        {choicesShowing && (
          <div
            style={{
              position: 'absolute',
              top: '110%',
              border: '1px solid #337ab7',
            }}
          >
            {choices.map((choice, index) => (
              <ListItem
                key={index}
                value={choice}
                click={() => handleListItemClick(choice)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface CurrentProps {
  value: number | string;
  toggle: (event: React.MouseEvent) => void;
}

const Current: FC<CurrentProps> = ({ value, toggle }) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = (): void => {
    setIsHovered(true);
  };

  const handleMouseLeave = (): void => {
    setIsHovered(false);
  };

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={toggle}
      style={{
        display: 'flex',
        height: '25px',
        width: `${CONTAINER_W}px`,
        backgroundColor: isHovered ? 'lightgray' : 'white',
        justifyContent: 'left',
        alignItems: 'center',
        cursor: 'pointer',
      }}
    >
      <span
        style={{
          paddingLeft: '10px',
          color: 'black',
          display: 'inline-block',
          whiteSpace: 'nowrap',
          flex: 1,
          minWidth: 0,
          maxWidth: `${CONTAINER_W - 20}px`,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        }}
      >
        {value}
      </span>
      <span style={{ flexShrink: 0, marginLeft: 4 }}>
        <DownTriangle />
      </span>
    </div>
  );
};

interface ListItemProps {
  value: string;
  click: () => void;
}

const ListItem: FC<ListItemProps> = ({ value, click }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [shouldScroll, setShouldScroll] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (textRef.current) {
      setShouldScroll(textRef.current.scrollWidth > CONTAINER_W - 10);
    }
  }, [value]);

  const handleMouseEnter = (): void => setIsHovered(true);
  const handleMouseLeave = (): void => setIsHovered(false);

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={click}
      style={{
        position: 'relative',
        display: 'flex',
        height: '25px',
        width: `${CONTAINER_W}px`,
        backgroundColor: isHovered ? 'lightgray' : 'white',
        padding: '5px 30px 5px 10px',
        cursor: 'pointer',
        color: 'black',
        zIndex: MODAL_Z_INDEX,
        overflow: 'hidden',
      }}
    >
      <span
        ref={textRef}
        style={{
          display: 'inline-block',
          whiteSpace: 'nowrap',
          animation: shouldScroll
            ? `scroll-text-listitem 2s linear infinite`
            : undefined,
        }}
      >
        {value}
      </span>
      <style>
        {`
          @keyframes scroll-text-listitem {
            0% { transform: translateX(0); }
            10% { transform: translateX(0); }
            90% { transform: translateX(calc(-100% + ${CONTAINER_W - 20}px)); }
            100% { transform: translateX(calc(-100% + ${CONTAINER_W - 20}px)); }
          }
        `}
      </style>
    </div>
  );
};
